import { Card, Icon, Spinner } from '@nike/eds'
import {
  plannedAndActualGoodsHolderCount,
  plannedAndActualPackedQuantityForShipment,
  shippingPoint,
  shipTo,
  measure,
  list,
} from '../../../common/utils/formatter'
import { DetailsField } from '../../../common/components/fields/DetailsField'
import { useEffect } from 'react'
import useRoleContext from '../../../common/hooks/useRoleContext'
import { get } from '../../../common/components/properties/Properties'
import './ShipmentDetails.scss'
import { useGetCustomerDetailsForPacklistLazyQuery } from '../../../generated/graphql'
import { GeneralTooltip } from '../../../common/components/generaltooltip/GeneralTooltip'
import TooltipPacklistShipToContent from '../../../common/components/tooltipcontent/tooltippacklistshiptocontent/TooltipPacklistShipToContent'
import TooltipCmrContent from '../../../common/components/tooltipcontent/tooltipcmrcontent/TooltipCmrContent'
import TooltipCarrierContent from '../../../common/components/tooltipcontent/tooltipcarriercontent/TooltipCarrierContent'

export function ShipmentDetails({ shipment }) {
  const vendor = shipment.shipmentVendors?.[0]
  const serviceLevelCode = vendor?.serviceLevelCode
  const integrator = vendor?.vendorCode
    ? ['MIXE', 'GTSS', 'CONS'].includes(vendor?.vendorCode)
      ? 'NIKE'
      : 'CENTIRO'
    : ''

  const hasCustomerDetailsAccess = useRoleContext().hasCustomerDetailsAccess()
  const [fetchCustomerDetails, queryResult] = useGetCustomerDetailsForPacklistLazyQuery({
    variables: { packlistNumber: shipment.packlistNumber },
    context: { page: 'shipments' },
  })
  const customerDetails = {
    ...queryResult?.data?.getCustomerDetailsForPacklist,
    serviceLevelCode,
  }

  //Use effect to force refresh when the page is revisited
  useEffect(() => {
    if (hasCustomerDetailsAccess) {
      fetchCustomerDetails()
    }
  }, [fetchCustomerDetails, hasCustomerDetailsAccess])

  return (
    <div>
      <Card className="shipment-details-fields" padding={12}>
        <div className="flex-row content-space-between">
          <DetailsField label="Shipping Point">
            {shippingPoint(shipment.shippingPointCode)}
          </DetailsField>

          <DetailsField label="Ship To Country">
            {shipment.shipmentShipToAddress?.countryCode}
          </DetailsField>
          <DetailsField label={'Ship To'}>
            {hasCustomerDetailsAccess ? (
              <GeneralTooltip
                body={<TooltipPacklistShipToContent data={customerDetails} />}
                anchorElement={
                  <>
                    {shipTo(shipment.shipmentShipToAddress)}
                    <Icon name="InfoCircle" size="s" className="eds-spacing--ml-4 no-flex-shrink" />
                  </>
                }
              />
            ) : (
              shipTo(shipment.shipmentShipToAddress)
            )}
          </DetailsField>
          <DetailsField label={''}></DetailsField>
        </div>
        <div className="flex-row content-space-between">
          <DetailsField label="Carrier & Hub">
            {vendor && (
              <GeneralTooltip
                body={
                  <TooltipCarrierContent vendorCode={vendor.vendorCode} hubCode={vendor.hubCode} />
                }
                anchorElement={
                  <>
                    {`${vendor.vendorCode} / ${vendor.hubCode}`}
                    <Icon name="InfoCircle" size="s" className="eds-spacing--ml-4 no-flex-shrink" />
                  </>
                }
              />
            )}
          </DetailsField>
          <DetailsField label="Integrator">{integrator}</DetailsField>
          <DetailsField label={'CMR'}>
            {get(shipment, 'proNumbers').length > 0 && (
              <GeneralTooltip
                body={<TooltipCmrContent data={shipment} />}
                anchorElement={
                  <>
                    {list(get(shipment, 'proNumbers'))}
                    <Icon name="InfoCircle" size="s" className="eds-spacing--ml-4 no-flex-shrink" />
                  </>
                }
              />
            )}
          </DetailsField>
          <DetailsField label="Traffic Instructions">
            {shipment.deliveryInstructionText}
          </DetailsField>
        </div>
        <div className="flex-row content-space-between">
          <DetailsField label="# Cartons (P/A)">
            {plannedAndActualGoodsHolderCount(shipment)}
          </DetailsField>
          <DetailsField label="# Units (P/A)">
            {plannedAndActualPackedQuantityForShipment(shipment)}
          </DetailsField>
          <DetailsField label="Volume">{measure(shipment?.calculatedGrossVolume)}</DetailsField>
          <DetailsField label="Weight">{measure(shipment?.calculatedGrossWeight)}</DetailsField>
        </div>
      </Card>
    </div>
  )
}
